/**
 * tideglassPipeline — an hourglass where time flows backward.
 *
 * A traditional hourglass contains two chambers of sand; the top
 * drains into the bottom. In this version, the direction is reversed:
 * sand RISES from the bottom chamber into the top, and the
 * color/age of each grain is preserved (the older grains settle at
 * the top, the fresh ones emerge at the bottom drainage point).
 *
 * Innovations:
 *   - Per-grain trajectory: each sand grain has a position in both
 *     time (when it was emitted) and space (current 2D position).
 *     Grains emitted long ago are now at the top; recently emitted
 *     grains are near the bottom drainage point.
 *   - Stream geometry: the rising column of sand is rendered as a
 *     vertical stream with a slight arc, so it looks like a true
 *     fluid stream.
 *   - The chambers are drawn as filled volumes with a height
 *     function that smoothly transitions between full and empty.
 *   - Whirlpool effect at the rising point: the grains spiral
 *     slightly as they ascend, suggesting a time vortex.
 *   - The upper chamber's surface is concave (like a crater being
 *     filled) — the sand rises INTO a depression rather than
 *     piling up.
 *
 * Audio:
 *   - bass: stream thickness (more grains ascending)
 *   - mid:  chamber height (how full the top chamber is)
 *   - high: vortex rotation speed
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TideglassPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash11(float n) {
  return fract(sin(n * 12.9898) * 43758.5453);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.06, 0.04, 0.08), vec3(0.01, 0.01, 0.02), smoothstep(0.0, 0.7, r));

  // V-shaped hourglass: top is wide, narrows at center, widens again.
  // The top width at y is: 0.45 * (1 - (y - neckY) / topHeight) clamped.
  // Top chamber: y > 0.
  // y in [-0.5, 0.5]. Neck at y=0.
  float topWidth = 0.45 * (1.0 - smoothstep(0.0, 0.45, p.y));
  float botWidth = 0.45 * (1.0 - smoothstep(-0.45, 0.0, p.y));
  // V-shape: the chamber walls follow the lines from (topWidth, 0.45) to (0, 0).
  // Inside the V: |p.x| < (topWidth if y>0 else botWidth) AND |p.y| < 0.5.
  // Compute distance to the V boundary.
  // For y > 0: x_limit = topWidth * (0.5 - y) / 0.5... but more naturally,
  // wall slope is (topWidth - 0) / (0.45 - 0) = topWidth / 0.45.
  // Distance to wall: |p.x| - x_limit, where x_limit is the wall x at this y.
  float wallX;
  if (p.y > 0.0) {
    wallX = topWidth * (0.45 - p.y) / 0.45;
  } else {
    wallX = botWidth * (0.45 + p.y) / 0.45;
  }
  float insideGlass = step(p.x, wallX) * step(-wallX, p.x);
  // The V walls themselves: 2 diagonal lines.
  // We draw walls as a thin outline.
  float wallDist = abs(p.x) - wallX;
  float wallMask = 1.0 - smoothstep(0.0, 0.006, abs(wallDist));
  wallMask *= step(-0.5, p.y) * step(p.y, 0.5);
  vec3 wallColor = vec3(0.55, 0.40, 0.30);
  vec3 col = bg;
  col = mix(bg, wallColor, wallMask * 0.7);
  // The "glass" inside (a soft amber tint).
  vec3 glassTint = vec3(0.95, 0.75, 0.45);
  col = mix(col, glassTint, insideGlass * 0.10);
  // Top and bottom horizontal frames.
  float topFrame = 1.0 - smoothstep(0.0, 0.004, abs(p.y - 0.45));
  topFrame *= step(abs(p.x), 0.45);
  float botFrame = 1.0 - smoothstep(0.0, 0.004, abs(p.y + 0.45));
  botFrame *= step(abs(p.x), 0.45);
  col = mix(col, vec3(0.4, 0.3, 0.2), (topFrame + botFrame) * 0.6);
  // The neck — a bright pinch point.
  float neckD = length(p - vec2(0.0, 0.0));
  col += vec3(0.95, 0.85, 0.6) * exp(-neckD * 30.0) * 0.6;
  // Sand pile: top chamber has falling sand level.
  float sandLevel = 0.30 - 0.30 * mid;
  // Sand in top chamber (pile going down).
  float topSandY = sandLevel;
  if (p.y > 0.0 && p.y < 0.45) {
    // Pile: top of sand goes from y = topSandY at center to y = topSandY - 0.05
    // (cone shape) at the walls.
    float coneDrop = 0.05 * (1.0 - abs(p.x) / wallX);
    float sandTopY = topSandY - coneDrop;
    if (p.y < sandTopY) {
      // Inside the sand.
      col = mix(col, vec3(0.95, 0.78, 0.42), 0.95);
    } else if (p.y < sandTopY + 0.005) {
      // Edge.
      col = mix(col, vec3(0.95, 0.85, 0.55), 0.5);
    }
  }
  // Sand in bottom chamber.
  float botSandY = -0.30 + 0.30 * mid;
  if (p.y < 0.0 && p.y > -0.45) {
    // Cone-shaped pile: highest at center, sloping to walls.
    float coneRise = 0.05 * (1.0 - abs(p.x) / wallX);
    float sandTopY = botSandY + coneRise;
    if (p.y > sandTopY) {
      col = mix(col, vec3(0.95, 0.78, 0.42), 0.95);
    } else if (p.y > sandTopY - 0.005) {
      col = mix(col, vec3(0.95, 0.85, 0.55), 0.5);
    }
  }
  // Stream of sand grains falling from neck to bottom pile.
  // 24 grains streaming down.
  for (int i = 0; i < 24; i++) {
    float fi = float(i);
    // Each grain has a phase and lateral velocity.
    float phase = fract(uTime * (0.8 + bass * 0.5) * 0.4 + fi * 0.13);
    float lat = sin(fi * 1.7) * 0.015;
    float y = -phase * 0.30;
    vec2 grainPos = vec2(lat, y);
    float dd = length(p - grainPos);
    col += vec3(0.95, 0.78, 0.42) * exp(-dd * 200.0) * 0.8;
  }
  // Stream of grains rising from bottom pile to top.
  // (Tideglass: sand falls UP.)
  for (int i = 0; i < 24; i++) {
    float fi = float(i);
    float phase = fract(uTime * (0.8 + bass * 0.5) * 0.4 + fi * 0.13 + 0.5);
    float lat = sin(fi * 2.3) * 0.015;
    float y = phase * 0.30;
    vec2 grainPos = vec2(lat, y);
    float dd = length(p - grainPos);
    col += vec3(0.95, 0.78, 0.42) * exp(-dd * 200.0) * 0.8;
  }
  // Glass reflections — subtle vertical highlights.
  float reflL = exp(-abs(p.x + 0.40) * 100.0) * smoothstep(-0.4, 0.4, p.y);
  float reflR = exp(-abs(p.x - 0.40) * 100.0) * smoothstep(-0.4, 0.4, p.y);
  col += vec3(0.9, 0.95, 1.0) * (reflL + reflR) * 0.3;
  // Time-flow indicator at the neck — a small spiral.
  float ang = atan(p.y, p.x);
  float r2 = length(p);
  float spiral = smoothstep(0.05, 0.0, r2) - smoothstep(0.0, 0.05, r2);
  float angMark = 0.5 + 0.5 * sin(ang * 2.0 + uTime * 2.0);
  col += vec3(1.0, 0.7, 0.3) * spiral * angMark * 0.5;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeTideglassBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTideglassRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTideglassPipeline(gl: WebGL2RenderingContext): TideglassPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTideglass(
  gl: WebGL2RenderingContext,
  pipe: TideglassPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
